import { useMemo } from "react";
import { useSdk } from "../../contexts/services/hooks/use-services-context";
import type { TodoItem } from "../../managers/mail/types/mail";
import { TODOS_KEY } from "../../managers/user/queries";
import { useSessionQuery, type LiveState } from "../session/useSessionQuery";

/** Todos acceptés de l'utilisateur, terminés compris. */
export function useUserTodos(): LiveState<TodoItem[]> {
    return useSessionQuery(TODOS_KEY, (sdk) =>
        sdk.userManager.fetchUserTodos(),
    );
}

export type Todos = {
    getUserTodos: (forceFetchTodosFromDb?: boolean) => Promise<TodoItem[]>;
    /** Coche ou décoche le todo dans `useUserTodos` avant la réponse du backend. */
    setTodoState: (
        internetMessageId: string,
        todoId: string,
        isDone: boolean,
    ) => Promise<boolean>;
    addTodoToDashboard: (
        internetMessageId: string,
        todoId: string,
    ) => Promise<boolean>;
    removeTodoFromDashboard: (
        internetMessageId: string,
        todoId: string,
    ) => Promise<boolean>;
};

export function useTodos(): Todos {
    const { userManager, mailManager, queryClient } = useSdk();

    return useMemo<Todos>(
        () => ({
            getUserTodos: (...args) => userManager.getUserTodos(...args),
            setTodoState: async (internetMessageId, todoId, isDone) => {
                const previous =
                    queryClient.getQueryData<TodoItem[]>(TODOS_KEY);
                if (previous) {
                    queryClient.setQueryData(
                        TODOS_KEY,
                        previous.map((todo) =>
                            todo.id === todoId
                                ? { ...todo, done: isDone }
                                : todo,
                        ),
                    );
                }

                try {
                    return await mailManager.setTodoState(
                        internetMessageId,
                        todoId,
                        isDone,
                    );
                } catch (error) {
                    if (previous) queryClient.setQueryData(TODOS_KEY, previous);
                    throw error;
                }
            },
            addTodoToDashboard: (...args) =>
                mailManager.addTodoToDashboard(...args),
            removeTodoFromDashboard: (...args) =>
                mailManager.removeTodoFromDashboard(...args),
        }),
        [userManager, mailManager, queryClient],
    );
}
